import path from "node:path";

import workerpool from "workerpool";

import { Environment, IdentityProvider, Logger } from "@abacus/common";
import { baseUrls as contractBaseUrls } from "@abacus/ows-contract-client";
import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";
import { baseUrls as eventBaseUrls } from "@abacus/ows-event-client";
import { OwsAbacusEventClient } from "@abacus/ows-event-client/fetch";
import { baseUrls as stateBaseUrls } from "@abacus/ows-state-client";
import { OwsAbacusStateClient } from "@abacus/ows-state-client/fetch";
import { createOwsLogger } from "@theorchard/ows-logger";

import env from "../env";
import { Neo4JClient } from "../neo4j";
import { getMaxWorkers } from "./utils";
import { SlidingWindowThrottler, Throttler } from "@abacus/throttlers";
import { SnowflakeClient, getConfig, getPrivateKey as getSnowflakePrivateKey } from "@abacus/snowflake-client";
import snowflake from "snowflake-sdk";
import { SecretsManager } from "@abacus/secrets-manager";
import { SecretsManagerClient } from "@aws-sdk/client-secrets-manager";

export function getDefaultAbacusContractClient(): OwsAbacusContractClient {
  return new OwsAbacusContractClient({
    auth: getIdentityProvider(),
    baseUrl: contractBaseUrls[env.ENVIRONMENT],
    logger: getLogger(),
    throttler: getThrottler(),
  });
}

export function getDefaultAbacusEventClient(): OwsAbacusEventClient {
  return new OwsAbacusEventClient({
    auth: getIdentityProvider(),
    baseUrl: eventBaseUrls[env.ENVIRONMENT],
    logger: getLogger(),
    throttler: getThrottler(),
  });
}

export function getDefaultAbacusStateClient(): OwsAbacusStateClient {
  return new OwsAbacusStateClient({
    auth: getIdentityProvider(),
    baseUrl: stateBaseUrls[env.ENVIRONMENT],
    logger: getLogger(),
    throttler: getThrottler(),
  });
}

export function getEnvNeo4JClient(): Neo4JClient {
  if (env.NEO4J_ENV_PASS == null) {
    throw new Error("Missing .env value for NEO4J_ENV_PASS");
  }
  if (env.NEO4J_ENV_USER == null) {
    throw new Error("Missing .env value for NEO4J_ENV_USER");
  }
  return new Neo4JClient({
    db: env.NEO4J_ENV_DB,
    env: env.ENVIRONMENT,
    host: env.NEO4J_ENV_HOST,
    isEncryptedConnection: true,
    pass: env.NEO4J_ENV_PASS,
    port: env.NEO4J_ENV_PORT,
    throttler: new SlidingWindowThrottler({
      duration: env.THROTTLE_DURATION,
      limit: env.THROTTLE_LIMIT,
    }),
    user: env.NEO4J_ENV_USER,
  });
}

let identityProvider: IdentityProvider | undefined = undefined;
export function getIdentityProvider(): IdentityProvider {
  identityProvider ??= new IdentityProvider({
    identityId: env.ORCHARD_IDENTITY_ID,
  });
  return identityProvider;
}

let logger: Logger | undefined = undefined;
export function getLogger(): Logger {
  logger ??= createOwsLogger({
    environment: env.ENVIRONMENT,
    loggerName: "owslogger",
    loggerLevel: "info",
    serviceName: "costco",
    serviceVersion: "1.0.0",
  });
  return logger;
}

export function getProdNeo4JClient(): Neo4JClient {
  if (env.NEO4J_PROD_PASS == null) {
    throw new Error("Missing .env value for NEO4J_PROD_PASS");
  }
  if (env.NEO4J_PROD_USER == null) {
    throw new Error("Missing .env value for NEO4J_PROD_USER");
  }
  return new Neo4JClient({
    db: env.NEO4J_PROD_DB,
    env: Environment.Prod,
    host: env.NEO4J_PROD_HOST,
    isEncryptedConnection: true,
    pass: env.NEO4J_PROD_PASS!,
    port: env.NEO4J_PROD_PORT,
    throttler: getThrottler(),
    user: env.NEO4J_PROD_USER!,
  });
}

let secretsClient: SecretsManagerClient | undefined = undefined;
export function getSecretsClient(): SecretsManagerClient {
  secretsClient ??= new SecretsManagerClient({
    profile: env.AWS_PROFILE,
    region: env.AWS_REGION,
  });
  return secretsClient;
}

let secretsManager: SecretsManager | undefined = undefined;
export function getSecretsManager(): SecretsManager {
  secretsManager ??= new SecretsManager({
    client: getSecretsClient(),
    environment: env.ENVIRONMENT,
    serviceName: env.APPLICATION_NAME,
  });
  return secretsManager;
}

let snowflakeClient: SnowflakeClient | undefined = undefined;
export async function getSnowflakeClient(): Promise<SnowflakeClient> {
  snowflakeClient ??= new SnowflakeClient({
    database: env.SNOWFLAKE_DATABASE,
    environment: env.ENVIRONMENT,
    logger: getLogger(),
    pool: await getSnowflakePool(),
  });
  return snowflakeClient;
}

let snowflakePool: snowflake.Pool<snowflake.Connection> | undefined = undefined;
export async function getSnowflakePool(): Promise<
  snowflake.Pool<snowflake.Connection>
> {
  // Check if pool exists
  if (snowflakePool != null) {
    return snowflakePool;
  }

  let snowflakeConfig = getConfig(process.env);

  // If private key not provided in QA, UAT or Prod
  if (
    snowflakeConfig.privateKey == null &&
    snowflakeConfig.privateKeyPath == null &&
    [Environment.QA, Environment.UAT, Environment.Prod].includes(
      env.ENVIRONMENT,
    )
  ) {
    const secretsManager = getSecretsManager();
    const privateKey = await getSnowflakePrivateKey(secretsManager);
    snowflakeConfig = {
      ...snowflakeConfig,
      privateKey,
      privateKeyPass: undefined,
    };
  }

  snowflakePool = snowflake.createPool(snowflakeConfig);
  return snowflakePool;
}

export function getThrottler(): Throttler {
  return new SlidingWindowThrottler({
    duration: env.THROTTLE_DURATION,
    limit: env.THROTTLE_LIMIT,
  });
}

export function getWorkerPool(numValues: number): workerpool.Pool {
  const workerFilePath = path.resolve(__dirname, "./index.worker.js");
  const numChunks = Math.ceil(numValues / env.CHUNK_SIZE);
  const targetWorkers = Math.min(env.MAX_WORKERS, numChunks);
  const maxWorkers = getMaxWorkers(targetWorkers);
  return workerpool.pool(workerFilePath, { maxWorkers });
}
